// After Visit Instructions Content
// Static content for all after-visit instruction pages
// URGENCY LANGUAGE STANDARDIZED per urgencyGuidelines.js

export const afterVisitInstructions = [
  {
    id: 'kidney-stone-conservative',
    title: 'Kidney Stone – Conservative Management',
    description: 'Care instructions when surgery is not currently needed'
  },
  {
    id: 'kidney-stone-ed-followup',
    title: 'Kidney Stone – Post Emergency Department Follow-Up',
    description: 'Follow-up care after emergency department visit'
  },
  {
    id: 'ureteral-stent',
    title: 'Ureteral Stent Care',
    description: 'Managing your temporary ureteral stent'
  },
  {
    id: 'post-ureteroscopy',
    title: 'Post-Ureteroscopy / Laser Lithotripsy',
    description: 'Recovery after stone surgery'
  },
  {
    id: 'cystoscopy',
    title: 'Cystoscopy Aftercare',
    description: 'What to expect after bladder examination'
  },
  {
    id: 'prostate-biopsy',
    title: 'Prostate Biopsy Aftercare',
    description: 'Recovery instructions after prostate biopsy'
  },
  {
    id: 'elevated-psa',
    title: 'Elevated PSA – Observation',
    description: 'Understanding PSA monitoring'
  },
  {
    id: 'bph',
    title: 'Benign Prostatic Hyperplasia (BPH)',
    description: 'Managing enlarged prostate symptoms'
  },
  {
    id: 'urinary-retention-catheter',
    title: 'Acute Urinary Retention – Catheter Care',
    description: 'Caring for your urinary catheter'
  },
  {
    id: 'catheter-removal',
    title: 'Catheter Removal Instructions',
    description: 'What to expect after catheter removal'
  },
  {
    id: 'hematuria',
    title: 'Hematuria Evaluation',
    description: 'Follow-up for blood in urine'
  },
  {
    id: 'recurrent-uti',
    title: 'Recurrent Urinary Tract Infections',
    description: 'Managing and preventing UTIs'
  },
  {
    id: 'erectile-dysfunction',
    title: 'Erectile Dysfunction',
    description: 'Treatment options and follow-up care'
  },
  {
    id: 'testicular-pain',
    title: 'Testicular Pain – Non-Emergent',
    description: 'Managing testicular discomfort'
  },
  {
    id: 'vasectomy',
    title: 'Vasectomy Aftercare',
    description: 'Recovery after vasectomy procedure'
  },
  {
    id: 'post-prostate-outlet-surgery',
    title: 'Post Prostate Outlet Surgery Instructions',
    description: 'Recovery after TURP, laser prostate surgery, UroLift, or Rezum'
  },
  {
    id: 'warning-signs-guide',
    title: 'Warning Signs: When to Seek Care',
    description: 'Know when to call your doctor or go to the emergency room'
  },
  {
    id: 'post-eswl',
    title: 'Post-ESWL (Shockwave Lithotripsy) Care',
    description: 'Recovery after shockwave treatment for kidney stones'
  },
  {
    id: 'post-pcnl',
    title: 'Post-PCNL (Percutaneous Nephrolithotomy) Care',
    description: 'Recovery after surgery for large kidney stones'
  },
  {
    id: 'post-bcg',
    title: 'BCG Therapy Aftercare',
    description: 'What to expect during and after BCG bladder treatments'
  },
  {
    id: 'post-turbt',
    title: 'Post-TURBT / Bladder Biopsy Care',
    description: 'Recovery after bladder tumor resection or biopsy'
  },
  {
    id: 'post-cystectomy',
    title: 'Post-Radical Cystectomy Care',
    description: 'Recovery after bladder removal surgery'
  },
  {
    id: 'post-prostatectomy',
    title: 'Post-Radical Prostatectomy Care',
    description: 'Recovery after prostate cancer surgery'
  },
  {
    id: 'post-orchiectomy',
    title: 'Post-Orchiectomy Care',
    description: 'Recovery after testicle removal surgery'
  },
  {
    id: 'post-kidney-ablation',
    title: 'Post-Kidney Ablation Care',
    description: 'Recovery after cryoablation or radiofrequency ablation'
  },
  {
    id: 'post-partial-nephrectomy',
    title: 'Post-Partial Nephrectomy Care',
    description: 'Recovery after kidney-sparing surgery'
  },
  {
    id: 'post-hydrocelectomy',
    title: 'Post-Hydrocelectomy Care',
    description: 'Recovery after hydrocele repair surgery'
  },
  {
    id: 'post-spermatocelectomy',
    title: 'Post-Spermatocelectomy Care',
    description: 'Recovery after spermatocele removal surgery'
  },
  {
    id: 'post-varicocelectomy',
    title: 'Post-Varicocelectomy Care',
    description: 'Recovery after varicocele repair surgery'
  },
  {
    id: 'post-circumcision',
    title: 'Post-Circumcision Care',
    description: 'Recovery after circumcision surgery'
  },
  {
    id: 'suprapubic-catheter',
    title: 'Suprapubic Catheter Care',
    description: 'Managing your suprapubic tube'
  },
  {
    id: 'nephrostomy-tube',
    title: 'Nephrostomy Tube Care',
    description: 'Caring for your kidney drain'
  },
  {
    id: 'self-catheterization',
    title: 'Intermittent Self-Catheterization',
    description: 'Learning to catheterize yourself'
  },
  {
    id: 'active-surveillance-prostate',
    title: 'Active Surveillance for Prostate Cancer',
    description: 'Monitoring low-risk prostate cancer'
  }
];

export const afterVisitContent = {
  'kidney-stone-conservative': {
    title: 'Kidney Stone – Conservative Management',
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d8/Blausen_0595_KidneyStones.png/600px-Blausen_0595_KidneyStones.png',
      alt: 'Diagram showing kidney stones in the urinary tract',
      source: 'Blausen Medical / Wikimedia Commons (CC BY 3.0)',
      caption: 'Kidney stones can form in the kidney and may travel down the ureter'
    },
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You were seen for a kidney stone that does not currently require surgery.'
      },
      {
        heading: 'What to Expect',
        content: 'Pain may come and go. Blood in the urine is common. Urinary urgency may occur.'
      },
      {
        heading: 'What You Should Do',
        items: ['Drink fluids to keep urine light yellow', 'Take medications as prescribed', 'Use a urine strainer if instructed']
      },
      {
        heading: 'Normal Symptoms',
        items: ['Mild blood in urine', 'Intermittent flank or groin pain']
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: ['Fever ≥100.4°F (38°C)', 'Pain not controlled with prescribed medications', 'Persistent vomiting preventing fluid intake']
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: ['Fever ≥101°F (38.3°C) with flank pain or chills', 'Complete inability to urinate', 'Severe uncontrolled pain not responding to medication']
      }
    ]
  },
  'kidney-stone-ed-followup': {
    title: 'Kidney Stone – Post Emergency Department Follow-Up',
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'Follow-up after emergency department evaluation for kidney stones.'
      },
      {
        heading: 'What to Expect',
        content: 'Symptoms may fluctuate while the stone passes.'
      },
      {
        heading: 'What You Should Do',
        items: ['Continue hydration', 'Take prescribed medications', 'Attend scheduled follow-up']
      },
      {
        heading: 'Normal Symptoms',
        items: ['Mild blood in urine', 'Intermittent discomfort']
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: ['Fever ≥100.4°F (38°C)', 'Worsening pain despite medication']
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: ['Fever ≥101°F (38.3°C) with chills', 'Severe uncontrolled pain', 'Complete inability to urinate']
      }
    ]
  },
  'ureteral-stent': {
    title: 'Ureteral Stent Care',
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/5/53/Ureteral_stent.jpg',
      alt: 'Diagram of a ureteral stent',
      source: 'Wikimedia Commons (Public Domain)',
      caption: 'A ureteral stent is a thin tube placed in the ureter to help urine drain from the kidney'
    },
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You currently have a temporary ureteral stent in place.'
      },
      {
        heading: 'What to Expect',
        content: 'Urinary urgency, frequency, flank discomfort, and blood in the urine are common.'
      },
      {
        heading: 'What You Should Do',
        items: ['Stay well hydrated', 'Avoid heavy exertion', 'Take medications as directed']
      },
      {
        heading: 'Normal Symptoms',
        items: ['Burning with urination', 'Blood-tinged urine']
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: ['Fever ≥100.4°F (38°C)', 'Severe pain not relieved by medication']
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: ['Complete inability to urinate', 'Fever ≥101°F (38.3°C) with flank pain or chills']
      }
    ]
  },
  'post-ureteroscopy': {
    title: 'Post-Ureteroscopy / Laser Lithotripsy',
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You recently underwent stone surgery.'
      },
      {
        heading: 'What to Expect',
        content: 'Blood in urine and discomfort are common for several days.'
      },
      {
        heading: 'What You Should Do',
        items: ['Drink fluids', 'Avoid heavy lifting for one week', 'Take medications as prescribed']
      },
      {
        heading: 'Normal Symptoms',
        items: ['Mild blood in urine', 'Urinary frequency']
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: ['Fever ≥100.4°F (38°C)', 'Increasing pain despite medication']
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: ['Fever ≥101°F (38.3°C) with chills', 'Severe pain not responding to prescribed medication', 'Complete inability to urinate']
      }
    ]
  },
  'cystoscopy': {
    title: 'Cystoscopy Aftercare',
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/38/Diagram_showing_a_cystoscopy_for_a_man_and_a_woman_CRUK_064.svg/500px-Diagram_showing_a_cystoscopy_for_a_man_and_a_woman_CRUK_064.svg.png',
      alt: 'Diagram showing cystoscopy procedure',
      source: 'Cancer Research UK / Wikimedia Commons (CC BY-SA 4.0)',
      caption: 'A cystoscope is inserted through the urethra to examine the bladder'
    },
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You had a cystoscopy performed.'
      },
      {
        heading: 'What to Expect',
        content: 'Burning and mild blood in urine for 24–48 hours is common.'
      },
      {
        heading: 'What You Should Do',
        items: ['Increase fluid intake', 'Use prescribed urinary medications if provided']
      },
      {
        heading: 'Normal Symptoms',
        items: ['Burning with urination', 'Light bleeding']
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: ['Symptoms persisting beyond 48 hours', 'Fever ≥100.4°F (38°C)']
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: ['Complete inability to urinate', 'Fever ≥101°F (38.3°C) with chills']
      }
    ]
  },
  'prostate-biopsy': {
    title: 'Prostate Biopsy Aftercare',
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/12/Diagram_showing_a_prostate_biopsy_CRUK_472.svg/500px-Diagram_showing_a_prostate_biopsy_CRUK_472.svg.png',
      alt: 'Diagram showing prostate biopsy procedure',
      source: 'Cancer Research UK / Wikimedia Commons (CC BY-SA 4.0)',
      caption: 'During a prostate biopsy, small tissue samples are taken from the prostate'
    },
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You recently had a prostate biopsy performed.'
      },
      {
        heading: 'What to Expect',
        content: 'Blood in urine, semen, and stool is common for several days to weeks.'
      },
      {
        heading: 'What You Should Do',
        items: ['Drink plenty of fluids', 'Take antibiotics as prescribed', 'Avoid strenuous activity for 24–48 hours', 'Avoid blood thinners as directed']
      },
      {
        heading: 'Normal Symptoms',
        items: ['Blood in urine for 1–2 weeks', 'Blood in semen for several weeks', 'Blood in stool for 1–2 days', 'Mild rectal soreness']
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: ['Fever ≥100.4°F (38°C)', 'Difficulty urinating', 'Heavy rectal bleeding', 'Increasing pain or swelling']
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: ['Fever ≥101°F (38.3°C) with chills', 'Complete inability to urinate', 'Signs of severe infection (confusion, rapid heartbeat, severe weakness)']
      }
    ]
  },
  'elevated-psa': {
    title: 'Elevated PSA – Observation',
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You are being monitored for an elevated PSA level.'
      },
      {
        heading: 'What to Expect',
        content: 'Periodic blood tests and possible imaging or biopsy may be recommended based on PSA trends.'
      },
      {
        heading: 'What You Should Do',
        items: ['Attend all scheduled follow-up appointments', 'Have PSA levels checked as recommended', 'Report any new urinary symptoms']
      },
      {
        heading: 'Normal Symptoms',
        items: ['No symptoms are expected from elevated PSA alone', 'Some men may have unrelated urinary symptoms']
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: ['New difficulty urinating', 'Blood in urine', 'New bone pain']
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: ['Complete inability to urinate', 'Severe bone pain limiting mobility']
      }
    ]
  },
  'bph': {
    title: 'Benign Prostatic Hyperplasia (BPH)',
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/c/cd/Benign_Prostatic_Hyperplasia_nci-vol-7137-300.jpg',
      alt: 'Diagram comparing normal prostate to enlarged prostate',
      source: 'National Cancer Institute / Wikimedia Commons (Public Domain)',
      caption: 'Left: Normal prostate. Right: Enlarged prostate (BPH) compressing the urethra.'
    },
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You were evaluated for an enlarged prostate causing urinary symptoms.'
      },
      {
        heading: 'What to Expect',
        content: 'Symptoms may improve with medication over several weeks. Some symptoms may persist.'
      },
      {
        heading: 'What You Should Do',
        items: ['Take medications as prescribed', 'Limit fluids before bedtime', 'Avoid caffeine and alcohol', 'Urinate when you first feel the urge']
      },
      {
        heading: 'Normal Symptoms',
        items: ['Frequent urination', 'Weak stream', 'Nighttime urination', 'Feeling of incomplete emptying']
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: ['Blood in urine', 'Significant worsening of symptoms despite medication', 'Signs of UTI (burning, fever)']
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: ['Complete inability to urinate', 'Severe lower abdominal pain with inability to urinate']
      }
    ]
  },
  'urinary-retention-catheter': {
    title: 'Acute Urinary Retention – Catheter Care',
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/a/aa/Foley_catheter_in_place.png',
      alt: 'Diagram showing Foley catheter placement in the bladder',
      source: 'NIH / Wikimedia Commons (Public Domain)',
      caption: 'A Foley catheter drains urine from the bladder into a collection bag'
    },
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'A catheter was placed because you were unable to urinate.'
      },
      {
        heading: 'What to Expect',
        content: 'The catheter will drain urine continuously into a bag. Some discomfort is normal.'
      },
      {
        heading: 'What You Should Do',
        items: ['Keep the drainage bag below bladder level', 'Empty the bag regularly', 'Keep the catheter secured to your leg', 'Drink fluids to keep urine flowing', 'Attend your follow-up appointment']
      },
      {
        heading: 'Normal Symptoms',
        items: ['Mild discomfort around the catheter', 'Urge to urinate despite the catheter', 'Small amount of blood in urine initially']
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: ['Catheter falls out', 'No urine draining for 4+ hours', 'Fever ≥100.4°F (38°C)', 'Increasing pain around catheter']
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: ['Catheter comes out AND you cannot urinate', 'Fever ≥101°F (38.3°C) with chills', 'Severe abdominal pain with no urine output']
      }
    ]
  },
  'catheter-removal': {
    title: 'Catheter Removal Instructions',
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'Your urinary catheter has been removed.'
      },
      {
        heading: 'What to Expect',
        content: 'Burning and frequency are common for 24–48 hours. It may take time for normal urination to return.'
      },
      {
        heading: 'What You Should Do',
        items: ['Drink plenty of fluids', 'Urinate when you feel the urge', 'Take medications as prescribed', 'Avoid straining']
      },
      {
        heading: 'Normal Symptoms',
        items: ['Burning with urination', 'Increased frequency', 'Mild blood in urine', 'Sense of urgency']
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: ['Unable to urinate within 6–8 hours of catheter removal', 'Fever ≥100.4°F (38°C)', 'Severe burning or pain']
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: ['Complete inability to urinate with increasing bladder discomfort', 'Fever ≥101°F (38.3°C) with chills']
      }
    ]
  },
  'hematuria': {
    title: 'Hematuria Evaluation',
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/7/79/Sources_of_Hematuria_by_Anatomic_Location.png',
      alt: 'Diagram showing sources of blood in urine by anatomic location',
      source: 'Wikimedia Commons (CC BY-SA 4.0)',
      caption: 'Blood in urine can originate from various parts of the urinary tract'
    },
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You are being evaluated for blood in your urine.'
      },
      {
        heading: 'What to Expect',
        content: 'Testing may include urine tests, imaging, and cystoscopy to determine the cause.'
      },
      {
        heading: 'What You Should Do',
        items: ['Complete all recommended tests', 'Stay well hydrated', 'Avoid blood thinners if directed', 'Attend follow-up appointments']
      },
      {
        heading: 'Normal Symptoms',
        items: ['Blood in urine may come and go', 'Urine color may vary from pink to red']
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: ['Heavy bleeding with clots', 'New difficulty urinating', 'Pain with urination']
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: ['Complete inability to urinate due to clots', 'Dizziness or lightheadedness from bleeding', 'Severe pain with heavy bleeding']
      }
    ]
  },
  'recurrent-uti': {
    title: 'Recurrent Urinary Tract Infections',
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/e/e5/Bladder_Infection.png',
      alt: 'Diagram illustrating bladder infection',
      source: 'BruceBlaus / Wikimedia Commons (CC BY-SA 4.0)',
      caption: 'Urinary tract infections occur when bacteria enter the bladder'
    },
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You are being managed for recurrent urinary tract infections.'
      },
      {
        heading: 'What to Expect',
        content: 'Further evaluation may be needed. Preventive strategies can reduce infection frequency.'
      },
      {
        heading: 'What You Should Do',
        items: ['Take antibiotics as prescribed', 'Drink plenty of water daily', 'Urinate frequently and completely', 'Practice good hygiene', 'Consider preventive measures as discussed']
      },
      {
        heading: 'Normal Symptoms',
        items: ['Symptoms should improve within 24–48 hours of starting antibiotics']
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: ['Symptoms not improving after 48 hours of antibiotics', 'New symptoms develop', 'Blood in urine']
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: ['Fever ≥101°F (38.3°C) with chills', 'Severe back or flank pain with fever', 'Nausea and vomiting preventing medication intake', 'Confusion or altered mental status']
      }
    ]
  },
  'erectile-dysfunction': {
    title: 'Erectile Dysfunction',
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You were evaluated for erectile dysfunction.'
      },
      {
        heading: 'What to Expect',
        content: 'Treatment may include medications, lifestyle changes, or other therapies. Results vary.'
      },
      {
        heading: 'What You Should Do',
        items: ['Take medications as prescribed', 'Follow timing instructions for medications', 'Make recommended lifestyle changes', 'Attend follow-up appointments']
      },
      {
        heading: 'Normal Symptoms',
        items: ['Medications may cause mild headache or flushing', 'Improvement may take several attempts']
      },
      {
        heading: 'Routine Follow-Up Recommended If',
        warning: false,
        urgencyLevel: 'routine',
        items: ['Medication side effects are bothersome', 'No improvement after adequate trial', 'Questions about treatment options']
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: ['Erection lasting more than 4 hours (priapism)', 'Chest pain after taking ED medication', 'Vision changes after taking medication']
      }
    ]
  },
  'testicular-pain': {
    title: 'Testicular Pain – Non-Emergent',
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/93/Diagram_of_the_testicles_CRUK_048.svg/400px-Diagram_of_the_testicles_CRUK_048.svg.png',
      alt: 'Diagram of testicular anatomy',
      source: 'Cancer Research UK / Wikimedia Commons (CC BY-SA 4.0)',
      caption: 'Anatomy of the testicle showing the epididymis where pain often originates'
    },
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You were evaluated for testicular discomfort that is not an emergency.'
      },
      {
        heading: 'What to Expect',
        content: 'Pain may persist for days to weeks. Follow-up may be needed if symptoms do not improve.'
      },
      {
        heading: 'What You Should Do',
        items: ['Wear supportive underwear', 'Apply ice as directed', 'Take anti-inflammatory medications as prescribed', 'Avoid strenuous activity']
      },
      {
        heading: 'Normal Symptoms',
        items: ['Mild aching or discomfort', 'Symptoms that come and go']
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: ['Pain gradually worsening over days', 'New swelling or redness', 'Fever ≥100.4°F (38°C)']
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: ['SUDDEN severe testicular pain (possible torsion)', 'Testicle appears higher or rotated', 'Nausea/vomiting with sudden testicular pain']
      }
    ]
  },
  'vasectomy': {
    title: 'Vasectomy Aftercare',
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e2/Vasectomy_diagram-en.svg/500px-Vasectomy_diagram-en.svg.png',
      alt: 'Diagram showing vasectomy procedure anatomy',
      source: 'KDS4444 / Wikimedia Commons (CC BY-SA)',
      caption: 'A vasectomy involves cutting the vas deferens to prevent sperm from reaching semen'
    },
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You recently had a vasectomy performed.'
      },
      {
        heading: 'What to Expect',
        content: 'Mild pain and swelling are normal for several days. Full recovery takes about one week.'
      },
      {
        heading: 'What You Should Do',
        items: ['Rest for 24–48 hours', 'Apply ice packs as directed', 'Wear supportive underwear', 'Avoid heavy lifting for one week', 'Avoid sexual activity for one week', 'Complete follow-up semen analysis as instructed']
      },
      {
        heading: 'Normal Symptoms',
        items: ['Mild pain and swelling', 'Bruising', 'Small amount of blood at incision site']
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: ['Increasing pain or swelling after 48 hours', 'Fever ≥100.4°F (38°C)', 'Signs of infection at incision (redness, pus)', 'Bleeding that does not stop with pressure']
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: ['Severe scrotal swelling (size of grapefruit or larger)', 'Fever ≥101°F (38.3°C) with chills', 'Heavy uncontrolled bleeding']
      }
    ]
  },

  'post-prostate-outlet-surgery': {
    title: 'Post Prostate Outlet Surgery Instructions',
    sections: [
      {
        heading: 'Applies To',
        content: 'These instructions apply to surgeries for benign prostatic hyperplasia (BPH) including TURP (transurethral resection of prostate), laser prostatectomy (HoLEP, GreenLight/PVP), UroLift, and Rezum water vapor therapy.'
      },
      {
        heading: 'Immediately After Surgery',
        content: 'You will have a urinary catheter in place. This is temporary and helps the bladder heal. The catheter is typically removed within 1-3 days depending on the procedure.'
      },
      {
        heading: 'What to Expect',
        items: [
          'Blood in urine — normal for 2-4 weeks, may appear pink to red',
          'Burning with urination — common for first few weeks',
          'Urgency and frequency — bladder needs time to adjust',
          'Small blood clots — may pass in urine',
          'Weak stream initially — improves over 4-6 weeks',
          'Urinary leakage — temporary, usually resolves in weeks'
        ]
      },
      {
        heading: 'Activity Guidelines',
        items: [
          'No heavy lifting (>10 lbs) for 2-4 weeks',
          'No strenuous exercise for 2-4 weeks',
          'Avoid straining with bowel movements (use stool softener)',
          'No sexual activity for 2-4 weeks (or until cleared)',
          'Avoid sitting for prolonged periods',
          'Walking is encouraged and helps recovery'
        ]
      },
      {
        heading: 'Diet and Fluids',
        items: [
          'Drink plenty of water (8-10 glasses daily) to flush bladder',
          'Avoid caffeine and alcohol — can irritate bladder',
          'Eat high-fiber foods to prevent constipation',
          'Avoid spicy foods initially if they worsen symptoms'
        ]
      },
      {
        heading: 'Medications',
        items: [
          'Take prescribed antibiotics as directed',
          'Use stool softeners to prevent straining',
          'Pain medication as needed (usually mild pain only)',
          'Continue or stop prostate medications as instructed'
        ]
      },
      {
        heading: 'Important Notes About Bleeding',
        content: 'Bleeding may increase around days 7-14 as healing tissue (scab) falls off. This is normal. Increase fluid intake and rest. Blood should not be bright red with clots preventing urination.'
      },
      {
        heading: 'Retrograde Ejaculation',
        content: 'After TURP and some laser procedures, semen may go into the bladder instead of out the penis during orgasm (dry orgasm). This is common and not harmful but does affect fertility. Sensation and function are usually preserved.'
      },
      {
        heading: 'Follow-Up',
        items: [
          'Catheter removal appointment (if applicable)',
          'Post-operative check: typically 2-4 weeks after surgery',
          'Uroflow test may be done to assess improvement',
          'Full benefit seen at 6-12 weeks'
        ]
      },
      {
        heading: 'Normal Recovery Symptoms',
        items: [
          'Pink or red urine for several weeks',
          'Occasional small clots',
          'Burning or stinging with urination',
          'Urgency and frequency',
          'Mild discomfort',
          'Fatigue'
        ]
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'Increasing blood in urine (darker red, more clots)',
          'Fever ≥100.4°F (38°C)',
          'Burning that worsens rather than improves',
          'Inability to urinate (after catheter removal)',
          'Significant pain not controlled with medication',
          'Signs of urinary infection (cloudy, foul-smelling urine)'
        ]
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: [
          'Complete inability to urinate with painful, full bladder',
          'Heavy bleeding with large clots blocking urination',
          'Fever ≥101°F (38.3°C) with chills',
          'Severe abdominal or pelvic pain',
          'Dizziness or lightheadedness from blood loss'
        ]
      }
    ]
  },

  'warning-signs-guide': {
    title: 'Warning Signs: When to Seek Care',
    sections: [
      {
        heading: 'About This Guide',
        content: 'This guide helps you recognize symptoms that need medical attention. Knowing when to call your doctor versus when to go to the emergency room can help you get the right care at the right time.'
      },
      {
        heading: 'EMERGENCY: Call 911 or Go to ER Immediately',
        warning: true,
        urgencyLevel: 'emergency',
        items: [
          'Inability to urinate with severe lower abdominal pain and distended bladder',
          'Heavy uncontrolled bleeding from any urologic site',
          'Fever ≥101°F (38.3°C) with shaking chills (possible sepsis)',
          'Sudden severe testicular pain (possible torsion — time-sensitive)',
          'Severe flank pain with high fever (infected kidney stone)',
          'Blood in urine with dizziness or lightheadedness',
          'Priapism — painful erection lasting >4 hours',
          'Paraphimosis — foreskin stuck behind glans (swelling)',
          'Autonomic dysreflexia in spinal cord injury patients (severe headache, high BP, sweating)'
        ]
      },
      {
        heading: 'URGENT: Contact Urologist Within 24-72 Hours',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'Fever ≥100.4°F (38°C) after any urologic procedure',
          'New visible blood in urine (gross hematuria)',
          'Increasing pain at surgical site',
          'Signs of wound infection (redness, swelling, pus, warmth)',
          'Inability to urinate after catheter removal',
          'Catheter not draining or leaking around it',
          'Severe burning with urination not improving',
          'Scrotal swelling after procedure',
          'New testicular lump or mass',
          'Worsening symptoms despite treatment'
        ]
      },
      {
        heading: 'ROUTINE: Schedule Appointment Within 1-2 Weeks',
        items: [
          'Mild blood in urine without other symptoms',
          'Persistent urinary symptoms (frequency, urgency)',
          'Erectile dysfunction concerns',
          'Mild testicular discomfort (no lump)',
          'Questions about PSA results',
          'Medication refills needed',
          'Slow improvement after procedure',
          'Follow-up on test results'
        ]
      },
      {
        heading: 'Fever Guidelines After Procedures',
        content: 'Fever after urologic procedures can indicate infection. Low-grade fever (<100.4°F) in the first 24 hours may be a normal response to surgery. Fever ≥100.4°F that persists or develops later, especially with chills or other symptoms, requires prompt evaluation.'
      },
      {
        heading: 'Blood in Urine Guidelines',
        items: [
          'After procedures: Expected for days to weeks depending on surgery',
          'Increasing blood or large clots: Contact your doctor',
          'Blood preventing urination: Seek emergency care',
          'New blood without recent procedure: Needs evaluation',
          'Pink-tinged urine: Usually not urgent',
          'Red or dark urine with clots: More concerning'
        ]
      },
      {
        heading: 'Pain Guidelines',
        items: [
          'Mild pain controlled with OTC medication: Normal after procedures',
          'Moderate pain: May need prescription medication',
          'Severe pain not controlled: Contact your doctor',
          'Sudden severe pain: May need emergency evaluation',
          'Pain with fever: More concerning — contact doctor'
        ]
      },
      {
        heading: 'Catheter Problems',
        items: [
          'No urine draining: Check for kinks, ensure bag below bladder',
          'Leaking around catheter: May need larger size or bladder spasm medication',
          'Blood in tubing: Small amount normal; large clots need attention',
          'Catheter fell out: Contact your doctor (do not reinsert yourself)',
          'Pain around catheter: Some discomfort normal; severe pain needs evaluation'
        ]
      },
      {
        heading: 'When in Doubt',
        content: 'If you are unsure whether your symptoms require immediate attention, it is better to call and ask. Your urologists office can help you determine the urgency. After hours, many practices have on-call coverage. The emergency room is appropriate for true emergencies but may have long waits for non-emergent issues.'
      }
    ]
  },

  'post-eswl': {
    title: 'Post-ESWL (Shockwave Lithotripsy) Care',
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You recently had ESWL (Extracorporeal Shock Wave Lithotripsy) to break up your kidney stone(s). The stone has been fragmented into smaller pieces that will pass in your urine over the coming days to weeks.'
      },
      {
        heading: 'What to Expect',
        items: [
          'Blood in urine — normal for several days',
          'Bruising on back or side at treatment site',
          'Flank discomfort or soreness',
          'Stone fragments passing in urine (may cause pain)',
          'Episodes of renal colic as fragments travel down ureter'
        ]
      },
      {
        heading: 'What You Should Do',
        items: [
          'Drink plenty of fluids (8-10 glasses daily) to help flush fragments',
          'Strain your urine to collect stone fragments (use provided strainer)',
          'Stay active — walking helps fragments pass',
          'Take pain medication as prescribed for colic episodes',
          'Take alpha-blocker medication if prescribed (helps ureter relax)'
        ]
      },
      {
        heading: 'Activity Guidelines',
        items: [
          'Rest for 24 hours after procedure',
          'Resume normal activities as tolerated',
          'Avoid heavy lifting for 48 hours',
          'Avoid contact sports for 1 week'
        ]
      },
      {
        heading: 'Normal Recovery Symptoms',
        items: [
          'Pink to red urine for 2-3 days',
          'Skin bruising at treatment site',
          'Mild flank soreness',
          'Intermittent cramping as fragments pass',
          'Temporary increase in urinary frequency'
        ]
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'Fever ≥100.4°F (38°C)',
          'Pain not controlled with prescribed medication',
          'Persistent nausea or vomiting',
          'No stone fragments passing after 2 weeks',
          'Heavy bleeding with large clots'
        ]
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: [
          'Fever ≥101°F (38.3°C) with chills',
          'Severe pain not responding to medication',
          'Complete inability to urinate',
          'Dizziness or lightheadedness from bleeding'
        ]
      },
      {
        heading: 'Follow-Up',
        content: 'An X-ray or CT scan will be scheduled 2-4 weeks after your procedure to check if all stone fragments have passed. Bring any collected stone fragments to your appointment for analysis.'
      }
    ]
  },

  'post-pcnl': {
    title: 'Post-PCNL (Percutaneous Nephrolithotomy) Care',
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You recently had PCNL surgery to remove kidney stones through a small incision in your back. This is a more extensive procedure than other stone treatments.'
      },
      {
        heading: 'About Your Nephrostomy Tube',
        content: 'You may have a nephrostomy tube (drainage tube) coming out of your back. This drains urine directly from the kidney and is usually removed within 1-3 days. Keep the area clean and dry. The tube will be connected to a drainage bag.'
      },
      {
        heading: 'What to Expect',
        items: [
          'Blood in urine — normal for 1-2 weeks',
          'Drainage from nephrostomy site after tube removal (1-2 days)',
          'Flank pain and soreness at incision site',
          'Fatigue for 1-2 weeks',
          'You may also have a ureteral stent'
        ]
      },
      {
        heading: 'Nephrostomy Tube Care',
        items: [
          'Keep drainage bag below kidney level at all times',
          'Empty bag when half full',
          'Keep insertion site clean and dry',
          'Change dressing as instructed',
          'Do not pull on the tube'
        ]
      },
      {
        heading: 'Activity Guidelines',
        items: [
          'No heavy lifting (>10 lbs) for 4-6 weeks',
          'No strenuous activity for 2-4 weeks',
          'Walking is encouraged — start slowly',
          'No driving while on narcotic pain medication',
          'No swimming or baths until cleared by doctor'
        ]
      },
      {
        heading: 'Incision Care',
        items: [
          'Keep incision clean and dry',
          'Shower is OK 48 hours after surgery',
          'No soaking (baths, pools) for 2 weeks',
          'Some drainage from incision is normal for first few days'
        ]
      },
      {
        heading: 'Normal Recovery Symptoms',
        items: [
          'Blood-tinged urine for 1-2 weeks',
          'Flank soreness and bruising',
          'Fatigue',
          'Mild discomfort at incision site'
        ]
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'Fever ≥100.4°F (38°C)',
          'Increasing pain despite medication',
          'Heavy bleeding from incision or in urine',
          'Nephrostomy tube falls out or stops draining',
          'Signs of infection (redness, warmth, pus at incision)'
        ]
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: [
          'Fever ≥101°F (38.3°C) with chills',
          'Severe pain not controlled with medication',
          'Heavy bleeding that does not stop',
          'Dizziness or lightheadedness',
          'Difficulty breathing'
        ]
      },
      {
        heading: 'Follow-Up',
        content: 'You will have a follow-up appointment for tube removal and imaging to confirm all stones were removed. Some patients need a second procedure for complete stone clearance.'
      }
    ]
  },

  'post-bcg': {
    title: 'BCG Therapy Aftercare',
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You received BCG (Bacillus Calmette-Guérin) immunotherapy for bladder cancer. BCG is instilled into the bladder to stimulate your immune system to fight cancer cells.'
      },
      {
        heading: 'What to Expect After Each Treatment',
        items: [
          'Bladder symptoms starting 2-4 hours after treatment',
          'Burning with urination (most common)',
          'Frequent urination and urgency',
          'Blood in urine',
          'Flu-like symptoms (fever, fatigue, body aches)',
          'Symptoms typically last 24-72 hours'
        ]
      },
      {
        heading: 'Safety Precautions (First 6 Hours After Treatment)',
        items: [
          'Add 2 cups of bleach to toilet BEFORE urinating',
          'Let bleach sit for 15 minutes before flushing',
          'Sit to urinate (men) to avoid splashing',
          'Wash hands thoroughly after using bathroom',
          'Wash genital area after urinating'
        ]
      },
      {
        heading: 'Safety Precautions (First 48 Hours)',
        items: [
          'No sexual contact for 48 hours',
          'Use condom for sexual activity for 6 weeks',
          'Do not share towels',
          'Wash underwear separately',
          'Increase fluid intake to flush bladder'
        ]
      },
      {
        heading: 'Managing Side Effects',
        items: [
          'Drink plenty of fluids',
          'Take phenazopyridine (Pyridium) if prescribed for burning',
          'Use over-the-counter pain relievers for flu symptoms',
          'Rest if fatigued',
          'Use heating pad for bladder discomfort'
        ]
      },
      {
        heading: 'Normal Symptoms',
        items: [
          'Burning with urination for 1-2 days',
          'Low-grade fever (<101°F) for 24-48 hours',
          'Fatigue and body aches',
          'Blood-tinged urine',
          'Urgency and frequency'
        ]
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'Fever lasting more than 48 hours',
          'Symptoms worsening after 72 hours',
          'Severe bladder pain',
          'Joint pain or rash',
          'Heavy blood in urine'
        ]
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: [
          'Fever ≥101.5°F (38.6°C) lasting >24 hours',
          'Shaking chills (rigors)',
          'Severe fatigue or malaise lasting >72 hours',
          'Difficulty breathing',
          'Signs of systemic infection'
        ]
      },
      {
        heading: 'Treatment Schedule Reminder',
        content: 'BCG is typically given weekly for 6 weeks (induction), then maintenance doses at 3, 6, and 12 months. Complete the full course for best results. Side effects are signs that your immune system is responding.'
      }
    ]
  },

  'post-turbt': {
    title: 'Post-TURBT / Bladder Biopsy Care',
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You recently had a TURBT (transurethral resection of bladder tumor) or bladder biopsy. Tissue was removed from your bladder for diagnosis or treatment of bladder cancer.'
      },
      {
        heading: 'What to Expect',
        items: [
          'Blood in urine — normal for several days to 2 weeks',
          'Burning with urination',
          'Frequent urination and urgency',
          'Bladder spasms',
          'Feeling of incomplete emptying'
        ]
      },
      {
        heading: 'What You Should Do',
        items: [
          'Drink plenty of fluids (8-10 glasses daily)',
          'Take antibiotics as prescribed',
          'Use bladder spasm medication if provided',
          'Avoid straining with bowel movements',
          'Rest for the first few days'
        ]
      },
      {
        heading: 'Activity Guidelines',
        items: [
          'No heavy lifting (>10 lbs) for 2 weeks',
          'No strenuous exercise for 2 weeks',
          'Avoid sexual activity for 2 weeks',
          'Walking is encouraged',
          'May return to desk work in 1-3 days'
        ]
      },
      {
        heading: 'Catheter Care (If Applicable)',
        content: 'If you have a catheter, it will typically be removed within 24 hours to a few days. Keep the drainage bag below your bladder and empty it regularly.'
      },
      {
        heading: 'Normal Recovery Symptoms',
        items: [
          'Blood in urine (pink to red) for 1-2 weeks',
          'Small blood clots in urine',
          'Burning and urgency improving over days',
          'Increased frequency initially'
        ]
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'Fever ≥100.4°F (38°C)',
          'Heavy bleeding with large clots',
          'Inability to urinate',
          'Severe pain not controlled with medication',
          'Signs of infection (cloudy, foul-smelling urine)'
        ]
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: [
          'Complete inability to urinate with painful, full bladder',
          'Heavy bleeding with clots blocking urination',
          'Fever ≥101°F (38.3°C) with chills',
          'Severe abdominal pain'
        ]
      },
      {
        heading: 'Pathology Results',
        content: 'Your tissue samples are being analyzed. Results are typically available in 5-7 days. Your doctor will contact you to discuss the findings and next steps in your care.'
      }
    ]
  },

  'post-cystectomy': {
    title: 'Post-Radical Cystectomy Care',
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You recently had a radical cystectomy (bladder removal) for bladder cancer. This is major surgery with a significant recovery period. You now have a urinary diversion (ileal conduit, neobladder, or continent pouch).'
      },
      {
        heading: 'Hospital Recovery',
        items: [
          'Typical hospital stay is 5-10 days',
          'IV fluids and pain management',
          'Gradual return to eating',
          'Early walking is crucial for recovery',
          'Drains will be removed before discharge'
        ]
      },
      {
        heading: 'Ileal Conduit (Ostomy) Care',
        items: [
          'Empty pouch when 1/3 to 1/2 full',
          'Change pouch system every 3-5 days',
          'Check stoma for healthy pink/red color',
          'Keep skin around stoma clean and dry',
          'Watch for skin irritation'
        ]
      },
      {
        heading: 'Neobladder Care',
        items: [
          'Empty by relaxing and using abdominal muscles',
          'Void on a schedule (every 2-3 hours initially)',
          'Double void (wait and try again)',
          'Catheterize if unable to fully empty',
          'Set alarm for nighttime voiding initially'
        ]
      },
      {
        heading: 'Activity Guidelines',
        items: [
          'No heavy lifting (>10 lbs) for 6-8 weeks',
          'No driving for 2-4 weeks',
          'Walk daily, gradually increasing distance',
          'No strenuous exercise for 6-8 weeks',
          'Full recovery takes 2-3 months'
        ]
      },
      {
        heading: 'What You Should Do',
        items: [
          'Take all medications as prescribed',
          'Eat small, frequent meals',
          'Stay well hydrated',
          'Attend all follow-up appointments',
          'Monitor for signs of infection'
        ]
      },
      {
        heading: 'Normal Recovery Symptoms',
        items: [
          'Fatigue (may last weeks to months)',
          'Decreased appetite initially',
          'Mild abdominal discomfort',
          'Changes in bowel function',
          'Mucus in urine (from intestinal tissue)'
        ]
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'Fever ≥100.4°F (38°C)',
          'Increasing pain or redness at incision',
          'Stoma changes color (dark or pale)',
          'No urine output for 4+ hours',
          'Nausea and vomiting preventing eating',
          'Signs of dehydration'
        ]
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: [
          'Fever ≥101°F (38.3°C) with chills',
          'Severe abdominal pain or distension',
          'No urine output with abdominal swelling',
          'Heavy bleeding from stoma or incision',
          'Confusion or altered mental status',
          'Difficulty breathing'
        ]
      }
    ]
  },

  'post-prostatectomy': {
    title: 'Post-Radical Prostatectomy Care',
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You recently had a radical prostatectomy (prostate removal) for prostate cancer. The prostate and seminal vesicles were removed. You have a urinary catheter that will remain in place for 7-14 days.'
      },
      {
        heading: 'Catheter Care',
        items: [
          'Keep drainage bag below bladder level',
          'Empty bag when 2/3 full',
          'Use leg bag during day, larger bag at night',
          'Keep catheter secured to thigh',
          'Clean around catheter with soap and water daily',
          'Some blood in urine is normal'
        ]
      },
      {
        heading: 'What to Expect',
        items: [
          'Catheter remains 7-14 days',
          'Some incontinence after catheter removal (normal)',
          'Continence improves over 3-12 months',
          'Erections affected initially',
          'Erectile function may recover over 6-24 months'
        ]
      },
      {
        heading: 'Activity Guidelines',
        items: [
          'Walk daily — start with short distances',
          'No heavy lifting (>10 lbs) for 4-6 weeks',
          'No strenuous exercise for 4-6 weeks',
          'Climb stairs slowly',
          'No driving while on pain medication (usually 1-2 weeks)',
          'May return to desk work in 2-3 weeks'
        ]
      },
      {
        heading: 'Incontinence Management',
        items: [
          'Start Kegel exercises before catheter removal',
          'Use pads as needed after catheter removal',
          'Improvement expected over weeks to months',
          'Pelvic floor physical therapy may help'
        ]
      },
      {
        heading: 'Erectile Function',
        items: [
          'ED is common initially, even with nerve-sparing surgery',
          'Start ED medications early if prescribed (penile rehabilitation)',
          'Recovery can take 6-24 months',
          'Multiple treatment options available'
        ]
      },
      {
        heading: 'Normal Recovery Symptoms',
        items: [
          'Blood-tinged urine for 1-2 weeks',
          'Bladder spasms with catheter',
          'Constipation (use stool softeners)',
          'Fatigue for 2-4 weeks',
          'Swelling in scrotum or penis (resolves)'
        ]
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'Fever ≥100.4°F (38°C)',
          'Catheter not draining or falls out',
          'Increasing pain or swelling',
          'Heavy bleeding around catheter',
          'Signs of infection at incision sites'
        ]
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: [
          'Fever ≥101°F (38.3°C) with chills',
          'Complete inability to urinate after catheter removal',
          'Severe abdominal or pelvic pain',
          'Heavy uncontrolled bleeding',
          'Signs of blood clot (leg swelling, chest pain, shortness of breath)'
        ]
      },
      {
        heading: 'PSA Monitoring',
        content: 'Your first PSA test will be at 6-8 weeks after surgery. PSA should become undetectable. Regular PSA monitoring continues for years to watch for any recurrence.'
      }
    ]
  },

  'post-orchiectomy': {
    title: 'Post-Orchiectomy Care',
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You recently had an orchiectomy (testicle removal) for testicular cancer. The testicle was removed through an incision in your groin (inguinal approach).'
      },
      {
        heading: 'What to Expect',
        items: [
          'Pain and swelling in groin and scrotum',
          'Bruising extending to scrotum and thigh',
          'Numbness along incision and inner thigh',
          'Scrotal asymmetry (normal with one testicle)'
        ]
      },
      {
        heading: 'What You Should Do',
        items: [
          'Rest for first 24-48 hours',
          'Apply ice packs to groin area (20 min on, 20 min off)',
          'Wear supportive underwear or scrotal support',
          'Take pain medication as prescribed',
          'Take stool softeners to avoid straining'
        ]
      },
      {
        heading: 'Activity Guidelines',
        items: [
          'No heavy lifting for 2-4 weeks',
          'No strenuous activity for 2-3 weeks',
          'No sexual activity for 2-3 weeks',
          'Avoid stairs when possible for first week',
          'Walking is encouraged',
          'May return to desk work in 1-2 weeks'
        ]
      },
      {
        heading: 'Incision Care',
        items: [
          'Keep incision clean and dry',
          'Shower OK after 24-48 hours',
          'No baths, pools, or hot tubs for 2 weeks',
          'Steri-strips will fall off on their own'
        ]
      },
      {
        heading: 'Normal Recovery Symptoms',
        items: [
          'Swelling and bruising for 1-2 weeks',
          'Mild to moderate pain for first week',
          'Numbness in groin/thigh (may be permanent)',
          'Fatigue'
        ]
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'Fever ≥100.4°F (38°C)',
          'Increasing pain or swelling after 48 hours',
          'Signs of infection (redness, warmth, drainage)',
          'Bleeding that does not stop with pressure',
          'Difficulty urinating'
        ]
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: [
          'Fever ≥101°F (38.3°C) with chills',
          'Severe scrotal swelling',
          'Heavy uncontrolled bleeding',
          'Signs of blood clot (leg swelling, shortness of breath)'
        ]
      },
      {
        heading: 'Pathology and Follow-Up',
        content: 'Pathology results will be available in 5-7 days. These determine the type and stage of cancer and guide further treatment. Regular follow-up with blood tests (tumor markers) and imaging is essential.'
      },
      {
        heading: 'Living with One Testicle',
        items: [
          'One testicle produces sufficient testosterone',
          'Fertility usually preserved',
          'Sexual function should not be affected',
          'Testicular prosthesis available if desired'
        ]
      }
    ]
  },

  'post-kidney-ablation': {
    title: 'Post-Kidney Ablation Care',
    sections: [
      {
        heading: 'Reason for Visit',
        content: 'You recently had an ablation procedure (cryoablation or radiofrequency ablation) to treat a kidney tumor. The tumor was destroyed using extreme cold or heat through a small needle.'
      },
      {
        heading: 'What to Expect',
        items: [
          'Mild to moderate flank pain',
          'Blood in urine for a few days',
          'Post-ablation syndrome (flu-like symptoms)',
          'Bruising at needle site',
          'Most patients go home same day or next day'
        ]
      },
      {
        heading: 'Post-Ablation Syndrome',
        content: 'It is common to experience flu-like symptoms for 3-7 days including low-grade fever, fatigue, body aches, and general malaise. This is a normal response to the procedure and resolves on its own.'
      },
      {
        heading: 'What You Should Do',
        items: [
          'Rest for 24-48 hours',
          'Take pain medication as prescribed',
          'Use NSAIDs (ibuprofen) for flu-like symptoms if approved',
          'Stay well hydrated',
          'Keep needle site clean and dry'
        ]
      },
      {
        heading: 'Activity Guidelines',
        items: [
          'Light activity after 2-3 days',
          'No heavy lifting for 1-2 weeks',
          'No strenuous exercise for 1-2 weeks',
          'May return to work in 3-5 days',
          'Avoid contact sports for 2 weeks'
        ]
      },
      {
        heading: 'Normal Recovery Symptoms',
        items: [
          'Flank soreness for several days',
          'Blood-tinged urine for 1-3 days',
          'Low-grade fever (<101°F)',
          'Fatigue for up to 1 week',
          'Small bruise at needle entry site'
        ]
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'Fever ≥100.4°F (38°C) lasting >48 hours',
          'Increasing pain not controlled with medication',
          'Heavy bleeding in urine',
          'Severe flank or abdominal pain',
          'Signs of infection at needle site'
        ]
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: [
          'Fever ≥101°F (38.3°C) with chills',
          'Severe abdominal pain',
          'Heavy bleeding that does not stop',
          'Dizziness or lightheadedness',
          'Difficulty breathing'
        ]
      },
      {
        heading: 'Follow-Up and Surveillance',
        content: 'Imaging (CT or MRI) at 1-3 months confirms successful ablation. Long-term surveillance with imaging every 6 months initially, then annually, is essential to monitor for recurrence. Successful ablation shows no enhancement in the treated area.'
      }
    ]
  },

  'post-partial-nephrectomy': {
    title: 'Post-Partial Nephrectomy Care',
    sections: [
      {
        heading: 'About Your Surgery',
        content: 'You had a partial nephrectomy (kidney-sparing surgery) to remove a tumor while preserving as much healthy kidney tissue as possible. This may have been done robotically, laparoscopically, or through an open incision.'
      },
      {
        heading: 'Activity Restrictions',
        items: [
          'No heavy lifting (>10 lbs) for 4-6 weeks',
          'No strenuous exercise or abdominal exercises for 6 weeks',
          'Walking is encouraged starting day of surgery',
          'May drive once off narcotic pain medication',
          'Return to work: desk job 2-3 weeks, physical labor 6 weeks',
          'No contact sports or activities risking abdominal trauma for 8 weeks'
        ]
      },
      {
        heading: 'Incision Care',
        items: [
          'Keep incisions clean and dry for 48 hours',
          'May shower after 48 hours (no soaking/swimming for 2 weeks)',
          'Steri-strips will fall off on their own in 1-2 weeks',
          'Small amounts of drainage from incisions is normal initially'
        ]
      },
      {
        heading: 'Normal Recovery Symptoms',
        items: [
          'Incisional pain and soreness for 1-2 weeks',
          'Fatigue for 2-4 weeks',
          'Constipation (use stool softeners)',
          'Blood-tinged urine for several days',
          'Shoulder pain (from surgery positioning) – resolves in days'
        ]
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'Fever ≥100.4°F (38°C)',
          'Increasing redness, swelling, or drainage from incisions',
          'Persistent nausea or vomiting',
          'Heavy blood in urine or passing clots',
          'Worsening pain not controlled by medication'
        ]
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: [
          'Fever ≥101°F with chills',
          'Severe abdominal or flank pain',
          'Unable to urinate',
          'Chest pain or difficulty breathing',
          'Signs of wound infection (spreading redness, pus)'
        ]
      },
      {
        heading: 'Follow-Up',
        content: 'You will have a follow-up appointment in 1-2 weeks to check your incisions and recovery. Lab work and imaging (CT scan) will be scheduled at 3-6 months to assess kidney function and confirm complete tumor removal. Long-term surveillance is important.'
      }
    ]
  },

  'post-hydrocelectomy': {
    title: 'Post-Hydrocelectomy Care',
    sections: [
      {
        heading: 'About Your Surgery',
        content: 'You had a hydrocelectomy to remove a hydrocele – a fluid collection around the testicle. The surgery was performed through a small scrotal incision.'
      },
      {
        heading: 'Activity Restrictions',
        items: [
          'Rest at home for 24-48 hours',
          'No heavy lifting (>10 lbs) for 2 weeks',
          'Avoid strenuous activity for 2-3 weeks',
          'May return to desk work in 3-5 days',
          'Physical labor: wait 2-3 weeks',
          'No sexual activity for 2-3 weeks'
        ]
      },
      {
        heading: 'Scrotal Care',
        items: [
          'Wear supportive underwear (briefs) or a scrotal support for 1-2 weeks',
          'Apply ice packs (20 min on, 20 min off) for first 48 hours',
          'Keep incision clean and dry',
          'May shower after 24-48 hours',
          'Elevate scrotum on a rolled towel when lying down'
        ]
      },
      {
        heading: 'Normal Recovery Symptoms',
        items: [
          'Scrotal swelling and bruising for 1-2 weeks',
          'Discomfort and soreness for 1-2 weeks',
          'Swelling may temporarily be worse than before surgery',
          'Bruising may extend to penis or thighs – this is normal'
        ]
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'Fever ≥100.4°F (38°C)',
          'Increasing redness at incision',
          'Foul-smelling discharge from wound',
          'Significant increase in swelling after initial improvement',
          'Pain not controlled by prescribed medication'
        ]
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: [
          'Sudden severe scrotal pain',
          'Rapidly expanding scrotal swelling',
          'Fever with chills',
          'Signs of wound infection spreading'
        ]
      },
      {
        heading: 'Follow-Up',
        content: 'A follow-up appointment will be scheduled for 2-4 weeks after surgery. Full resolution of swelling may take 4-6 weeks. Recurrence is uncommon but possible.'
      }
    ]
  },

  'post-spermatocelectomy': {
    title: 'Post-Spermatocelectomy Care',
    sections: [
      {
        heading: 'About Your Surgery',
        content: 'You had a spermatocelectomy to remove a spermatocele – a benign cyst that developed in the epididymis (the coiled tube behind the testicle). The surgery was performed through a small scrotal incision.'
      },
      {
        heading: 'Activity Restrictions',
        items: [
          'Rest for 24-48 hours after surgery',
          'No heavy lifting (>10 lbs) for 2-4 weeks',
          'No strenuous exercise for 2-3 weeks',
          'May return to desk work in 2-3 days',
          'Physical labor: wait 2-3 weeks',
          'No sexual activity for 2-3 weeks'
        ]
      },
      {
        heading: 'Post-Operative Care',
        items: [
          'Wear supportive underwear or athletic supporter for 1-2 weeks',
          'Apply ice packs (20 min on/off) for first 48 hours to reduce swelling',
          'Keep incision clean and dry',
          'May shower after 24-48 hours – no baths or swimming for 2 weeks',
          'Stitches are usually dissolvable and absorb in 2-3 weeks'
        ]
      },
      {
        heading: 'Pain Management',
        items: [
          'Take prescribed pain medication as directed',
          'Over-the-counter acetaminophen (Tylenol) or ibuprofen may help for mild pain',
          'Pain typically improves significantly within 3-5 days'
        ]
      },
      {
        heading: 'Normal Recovery Symptoms',
        items: [
          'Scrotal swelling and bruising – normal for 1-2 weeks',
          'Mild to moderate discomfort for the first few days',
          'Swelling may temporarily make the scrotum appear larger than before',
          'Full swelling resolution may take 4-6 weeks'
        ]
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'Fever ≥100.4°F (38°C)',
          'Increasing redness or warmth around incision',
          'Pus or foul-smelling drainage from wound',
          'Pain that worsens instead of improving',
          'Difficulty urinating'
        ]
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: [
          'Sudden severe testicular pain',
          'Rapidly expanding scrotal swelling',
          'Fever with chills',
          'Signs of wound infection spreading (red streaks, hot skin)'
        ]
      },
      {
        heading: 'Fertility Considerations',
        content: 'If fertility is a concern, discuss with your urologist. The procedure may affect the epididymis on the operated side, which could impact sperm transport from that testicle. The other testicle typically provides adequate sperm for fertility.'
      },
      {
        heading: 'Follow-Up',
        content: 'A follow-up appointment will be scheduled for 2-4 weeks after surgery. Full resolution of swelling may take 4-8 weeks. Recurrence after complete surgical removal is rare.'
      }
    ]
  },

  'post-varicocelectomy': {
    title: 'Post-Varicocelectomy Care',
    sections: [
      {
        heading: 'About Your Surgery',
        content: 'You had a varicocelectomy to repair a varicocele (enlarged veins in the scrotum). This is commonly performed to improve fertility or relieve discomfort. The procedure was done through a small incision in the groin or abdomen.'
      },
      {
        heading: 'Activity Restrictions',
        items: [
          'Rest for 24-48 hours',
          'No heavy lifting (>10 lbs) for 2 weeks',
          'No strenuous exercise for 2-3 weeks',
          'May return to desk work in 2-3 days',
          'Physical labor: wait 2 weeks',
          'No sexual activity for 1-2 weeks'
        ]
      },
      {
        heading: 'Post-Operative Care',
        items: [
          'Wear supportive underwear for 1-2 weeks',
          'Ice packs to groin area as needed for first 48 hours',
          'Keep incision clean and dry',
          'May shower after 24 hours'
        ]
      },
      {
        heading: 'Normal Recovery Symptoms',
        items: [
          'Mild scrotal swelling and bruising',
          'Groin soreness for 1-2 weeks',
          'Numbness around incision (may last weeks to months)',
          'The varicocele may still be palpable initially – it takes time to resolve'
        ]
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'Fever ≥100.4°F (38°C)',
          'Increasing redness or drainage from incision',
          'Significant scrotal swelling',
          'Pain not improving after several days'
        ]
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: [
          'Sudden severe testicular pain',
          'Rapidly expanding swelling',
          'Fever with chills',
          'Signs of spreading infection'
        ]
      },
      {
        heading: 'Follow-Up and Fertility',
        content: 'Follow-up is typically scheduled at 2-4 weeks. If surgery was performed for infertility, a semen analysis is usually done at 3-4 months to assess improvement. Sperm parameters may continue to improve for up to 6-12 months after surgery.'
      }
    ]
  },

  'post-circumcision': {
    title: 'Post-Circumcision Care',
    sections: [
      {
        heading: 'About Your Surgery',
        content: 'You had a circumcision, surgical removal of the foreskin from the penis. This may have been done for medical reasons (phimosis, recurrent infections) or personal preference.'
      },
      {
        heading: 'Wound Care',
        items: [
          'Keep the area clean and dry',
          'Apply petroleum jelly (Vaseline) to the wound with each dressing change',
          'Change dressing daily or when soiled',
          'Loose gauze wrapping for first few days',
          'May shower after 24-48 hours – gently pat dry',
          'No soaking (baths, pools) for 2 weeks'
        ]
      },
      {
        heading: 'Activity Restrictions',
        items: [
          'Rest at home for 1-2 days',
          'Avoid strenuous activity for 1-2 weeks',
          'No sexual activity for 4-6 weeks (or until fully healed)',
          'Wear loose, comfortable underwear',
          'May return to work in 2-3 days (desk job)'
        ]
      },
      {
        heading: 'Normal Recovery Symptoms',
        items: [
          'Swelling of the penis for 1-2 weeks',
          'Bruising and discoloration',
          'Yellow discharge or crusting at incision line (normal healing)',
          'Stitches will dissolve on their own in 2-3 weeks',
          'Sensitivity of the glans (head) – improves over weeks'
        ]
      },
      {
        heading: 'Managing Erections',
        content: 'Nocturnal erections are normal and may cause discomfort during healing. This typically resolves as healing progresses. Wearing supportive underwear may help. Contact your doctor if erections cause significant pain or bleeding.'
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'Fever ≥100.4°F (38°C)',
          'Increasing redness spreading beyond incision',
          'Foul-smelling discharge or pus',
          'Stitches coming apart',
          'Persistent bleeding through dressing'
        ]
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: [
          'Heavy bleeding that does not stop with pressure',
          'Unable to urinate',
          'Fever with chills',
          'Severe pain not controlled by medication'
        ]
      },
      {
        heading: 'Follow-Up',
        content: 'A follow-up appointment is typically scheduled at 2-4 weeks to ensure proper healing. Full healing usually takes 4-6 weeks. Final cosmetic appearance may take several months.'
      }
    ]
  },

  'suprapubic-catheter': {
    title: 'Suprapubic Catheter Care',
    sections: [
      {
        heading: 'About Your Catheter',
        content: 'A suprapubic catheter (SPC) is a tube inserted through your lower abdomen directly into your bladder to drain urine. It was placed because you cannot empty your bladder normally. This may be temporary or long-term depending on your condition.'
      },
      {
        heading: 'Daily Care',
        items: [
          'Wash hands before and after touching the catheter',
          'Clean around the insertion site daily with soap and water',
          'Keep the site dry between cleanings',
          'Secure the catheter to your abdomen to prevent pulling',
          'Empty the drainage bag when it is half to two-thirds full',
          'Keep the bag below bladder level at all times'
        ]
      },
      {
        heading: 'Drainage Bag Care',
        items: [
          'Use a leg bag during the day for mobility',
          'Use a larger bedside bag at night',
          'Clean the bag daily with soap and water or dilute vinegar solution',
          'Replace bags as directed by your healthcare provider'
        ]
      },
      {
        heading: 'Staying Hydrated',
        content: 'Drink 6-8 glasses of water daily unless otherwise instructed. Adequate fluids help prevent infections and keep urine flowing. Cranberry juice or tablets may help prevent UTIs.'
      },
      {
        heading: 'Signs of Infection',
        items: [
          'Cloudy or foul-smelling urine',
          'Blood in urine (new or worsening)',
          'Fever or chills',
          'Pain at catheter site or in lower abdomen',
          'Redness, swelling, or discharge at insertion site'
        ]
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'Signs of urinary tract infection (above)',
          'Catheter is leaking around the insertion site',
          'Decreased or no urine output',
          'Catheter falls out (cover site and call immediately)',
          'Skin irritation or breakdown around site'
        ]
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: [
          'Fever ≥101°F with chills',
          'Severe abdominal pain',
          'Heavy bleeding from catheter site',
          'Catheter falls out and you cannot urinate',
          'Signs of severe infection (confusion, rapid heart rate)'
        ]
      },
      {
        heading: 'Catheter Changes',
        content: 'Suprapubic catheters need to be changed every 4-8 weeks by a healthcare provider. Never try to change it yourself unless you have been trained to do so. Keep track of your change schedule.'
      }
    ]
  },

  'nephrostomy-tube': {
    title: 'Nephrostomy Tube Care',
    sections: [
      {
        heading: 'About Your Nephrostomy Tube',
        content: 'A nephrostomy tube (also called a PCN or percutaneous nephrostomy) drains urine directly from your kidney through a tube that exits through your back or side. It was placed to relieve a blockage or as part of a procedure.'
      },
      {
        heading: 'Daily Care',
        items: [
          'Wash hands before and after caring for the tube',
          'Clean around the tube exit site daily with soap and water',
          'Keep the site covered with a clean, dry dressing',
          'Change the dressing daily or when wet/soiled',
          'Secure the tube to prevent pulling or dislodgement',
          'Empty the drainage bag when half full'
        ]
      },
      {
        heading: 'Activity Guidelines',
        items: [
          'Avoid strenuous activity or heavy lifting',
          'Be careful not to snag or pull on the tube',
          'May shower with the site covered (waterproof dressing)',
          'No swimming or soaking in water',
          'Sleep on the opposite side from the tube if comfortable'
        ]
      },
      {
        heading: 'What to Monitor',
        items: [
          'Urine color and clarity in the bag',
          'Amount of urine output (should be fairly consistent)',
          'Condition of the skin around the tube',
          'Position and security of the tube'
        ]
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'Decreased or no urine output for several hours',
          'Increasing cloudiness or foul smell of urine',
          'Fever ≥100.4°F (38°C)',
          'Pain at the tube site or in your side/back',
          'Leaking around the tube site',
          'Redness, swelling, or pus at the exit site'
        ]
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: [
          'Tube falls out completely (cover site with clean dressing)',
          'Heavy bleeding from the site',
          'Fever ≥101°F with chills or shaking',
          'Severe back or abdominal pain',
          'No urine output for 4+ hours with flank pain'
        ]
      },
      {
        heading: 'Tube Exchanges and Follow-Up',
        content: 'Nephrostomy tubes typically need to be exchanged every 2-3 months. Your doctor will schedule these exchanges. The tube may be removed once the underlying condition is resolved, or it may be needed long-term.'
      }
    ]
  },

  'self-catheterization': {
    title: 'Intermittent Self-Catheterization (ISC)',
    sections: [
      {
        heading: 'Why Self-Catheterization?',
        content: 'Intermittent self-catheterization allows you to empty your bladder when you are unable to urinate completely on your own. This prevents urinary retention, reduces infections, and protects your kidneys. With practice, most people become comfortable with this technique.'
      },
      {
        heading: 'Supplies Needed',
        items: [
          'Catheter (size prescribed by your doctor)',
          'Water-soluble lubricant (if not pre-lubricated catheter)',
          'Clean container or toilet to drain urine',
          'Soap and water or cleansing wipes for hand hygiene',
          'Mirror (may help during learning phase for women)'
        ]
      },
      {
        heading: 'General Technique',
        items: [
          'Wash hands thoroughly with soap and water',
          'Get into a comfortable position (sitting on toilet, standing, or lying down)',
          'Clean the urethral opening with mild soap and water or a wipe',
          'Lubricate the catheter tip (if not pre-lubricated)',
          'Gently insert the catheter until urine begins to flow',
          'Allow bladder to drain completely',
          'Slowly remove the catheter when urine stops',
          'Clean up and wash hands again'
        ]
      },
      {
        heading: 'How Often to Catheterize',
        content: 'Your doctor will tell you how often to catheterize based on your specific situation. This is typically every 4-6 hours or 4-6 times daily. Never go longer than 6-8 hours without catheterizing. Keep a log of times and amounts if asked.'
      },
      {
        heading: 'Tips for Success',
        items: [
          'Relax – tension makes insertion more difficult',
          'Take slow, deep breaths',
          'If you meet resistance, do not force – try a different angle',
          'Stay hydrated but time fluid intake to your catheterization schedule',
          'Travel with extra supplies'
        ]
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'Pain or burning during catheterization that persists',
          'Blood in urine (more than slight pink tinge)',
          'Cloudy, foul-smelling urine',
          'Unable to pass the catheter',
          'Fever ≥100.4°F (38°C)',
          'Leaking urine between catheterizations'
        ]
      },
      {
        heading: 'Seek Emergency Care Immediately If',
        warning: true,
        urgencyLevel: 'emergency',
        items: [
          'Unable to catheterize and bladder feels very full',
          'Fever ≥101°F with chills',
          'Severe pain in lower abdomen or back',
          'Heavy bleeding',
          'Signs of severe UTI (confusion, rapid heart rate)'
        ]
      },
      {
        heading: 'Catheter Care and Reuse',
        content: 'Follow your healthcare provider\'s instructions about catheter care. Some catheters are single-use, while others may be cleaned and reused. If reusing, wash with soap and water, rinse well, and store in a clean, dry container. Replace as directed.'
      }
    ]
  },

  'active-surveillance-prostate': {
    title: 'Active Surveillance for Prostate Cancer',
    sections: [
      {
        heading: 'What is Active Surveillance?',
        content: 'Active surveillance is a management strategy for low-risk prostate cancer that involves closely monitoring the cancer without immediate treatment. The goal is to avoid or delay the side effects of treatment while ensuring the cancer is detected early if it becomes more aggressive.'
      },
      {
        heading: 'Why Active Surveillance?',
        items: [
          'Your prostate cancer is low-risk (low PSA, low Gleason score, limited tumor)',
          'Many low-risk prostate cancers grow slowly and may never cause harm',
          'Treatment side effects (incontinence, erectile dysfunction) can significantly impact quality of life',
          'Treatment can be started if the cancer shows signs of progression',
          'This approach is recommended by major medical organizations for appropriate candidates'
        ]
      },
      {
        heading: 'Monitoring Schedule',
        items: [
          'PSA blood test: Every 3-6 months',
          'Digital rectal exam: Every 6-12 months',
          'Repeat prostate biopsy: Within 6-12 months of diagnosis, then periodically (every 1-3 years)',
          'MRI: May be used to monitor and guide biopsies',
          'Your schedule may vary based on your specific situation'
        ]
      },
      {
        heading: 'Signs That May Trigger Treatment',
        items: [
          'Rising PSA or increasing PSA velocity',
          'Biopsy showing higher grade cancer (Grade Group 2 or higher)',
          'Increase in the amount of cancer on biopsy',
          'Changes on MRI suggesting cancer growth',
          'Patient preference to proceed with treatment'
        ]
      },
      {
        heading: 'Living on Active Surveillance',
        items: [
          'Continue a healthy lifestyle (diet, exercise, not smoking)',
          'Keep all monitoring appointments – they are essential',
          'Understand that anxiety is normal and decreases over time for most men',
          'Talk to your doctor if anxiety is affecting your quality of life',
          'Know that you can choose treatment at any time if you change your mind'
        ]
      },
      {
        heading: 'Contact a Urologist Promptly (Within 24–72 Hours) If',
        warning: true,
        urgencyLevel: 'urgent',
        items: [
          'New urinary symptoms (blood in urine, difficulty urinating)',
          'New bone pain (especially back or hips)',
          'Unintentional weight loss',
          'Concerns about your cancer or surveillance plan'
        ]
      },
      {
        heading: 'Important Reminders',
        content: 'Active surveillance is not "doing nothing" – it is a proven management strategy that includes careful, regular monitoring. The vast majority of men on active surveillance who eventually need treatment are still curable. You remain in control of your care decisions.'
      }
    ]
  }
};
